<script setup>import { ref as _ref, computed as _computed } from 'vue';

  import { watch } from "vue";
  import { useDebounceFn } from "@vueuse/core";
  import { inventoryFilterUserPath } from "~/routes";
  import { fetchJson } from "~/recipal-app/controllers/shared/requests.js";

  const props = defineProps({
    userId: { type: Number, required: true },
    supplierId: { type: Number, default: null },
    supplierName: { type: String, default: null },
    excludedItemIds: { type: Array, default: () => [] },
  });

  const emit = defineEmits(["add"]);

  const PER_PAGE = 10;

  let expanded = _ref(false);
  let query = _ref("");
  let page = _ref(1);
  let totalEntries = _ref(0);
  let inventories = _ref([]);
  let selectedItems = _ref(new Map());
  let loading = _ref(false);
  let abortController = null;

  const totalPages = _computed(() => Math.max(1, Math.ceil(totalEntries.value / PER_PAGE)));

  const selectedCount = _computed(() => selectedItems.value.size);

  const allOnPageSelected = _computed(
    () => inventories.value.length > 0 && inventories.value.every((inv) => selectedItems.value.has(inv.id))
  );

  async function fetchPage() {
    if (!props.supplierId) return;
    if (abortController) abortController.abort();
    abortController = new AbortController();

    loading.value = true;
    try {
      const params = new URLSearchParams({
        q: query.value,
        per_page: PER_PAGE,
        page: page.value,
        supplier_id: props.supplierId,
        exclude_subrecipes: "true",
      });
      props.excludedItemIds.forEach((id) => params.append("exclude_ids[]", id));
      const url = `${inventoryFilterUserPath(props.userId)}?${params}`;
      const { data } = await fetchJson(url, { signal: abortController.signal });
      inventories.value = data.inventories;
      totalEntries.value = data.total_entries;
      const maxPage = Math.max(1, Math.ceil(totalEntries.value / PER_PAGE));
      if (page.value > maxPage) {
        page.value = maxPage;
        await fetchPage();
      }
    } catch (e) {
      if (e.name !== "AbortError") throw e;
    } finally {
      loading.value = false;
    }
  }

  const debouncedFetch = useDebounceFn(fetchPage, 300);

  function onQueryInput(event) {
    query.value = event.target.value;
    page.value = 1;
    debouncedFetch();
  }

  function goToPage(target) {
    if (target < 1 || target > totalPages.value) return;
    page.value = target;
    fetchPage();
  }

  function toggleExpanded() {
    if (!props.supplierId) return;
    expanded.value = !expanded.value;
    if (expanded.value && inventories.value.length === 0) fetchPage();
  }

  function toggleSelection(inv) {
    const next = new Map(selectedItems.value);
    if (next.has(inv.id)) next.delete(inv.id);
    else next.set(inv.id, inv);
    selectedItems.value = next;
  }

  function toggleAllOnPage() {
    const next = new Map(selectedItems.value);
    if (allOnPageSelected.value) {
      inventories.value.forEach((inv) => next.delete(inv.id));
    } else {
      inventories.value.forEach((inv) => next.set(inv.id, inv));
    }
    selectedItems.value = next;
  }

  function clearSelection() {
    selectedItems.value = new Map();
  }

  function addSelected() {
    const picked = [...selectedItems.value.values()];
    if (picked.length === 0) return;
    emit("add", picked);
    selectedItems.value = new Map();
  }

  function addOne(inv) {
    emit("add", [inv]);
  }

  watch(
    () => props.supplierId,
    () => {
      page.value = 1;
      query.value = "";
      selectedItems.value = new Map();
      inventories.value = [];
      totalEntries.value = 0;
      if (props.supplierId) fetchPage();
    },
    { immediate: true }
  );

  watch(
    () => props.excludedItemIds,
    () => {
      const next = new Map(selectedItems.value);
      props.excludedItemIds.forEach((id) => next.delete(id));
      if (next.size !== selectedItems.value.size) selectedItems.value = next;
      if (props.supplierId) fetchPage();
    }
  );
</script>

<template>
  <div v-if="supplierId" class="supplier-inventory-picker well">
    <button
      type="button"
      class="picker-heading"
      :aria-expanded="expanded"
      data-test="picker-toggle"
      @click="toggleExpanded"
    >
      <span class="picker-heading-text">
        <span class="picker-heading-label">
          Ingredients from
          <strong class="picker-heading-supplier has-text-link">{{
            supplierName || "supplier"
          }}</strong>
        </span>
        <span class="picker-heading-subtitle has-text-grey">
          {{ totalEntries }}
          {{ totalEntries === 1 ? "ingredient" : "ingredients" }} not in this order
        </span>
      </span>
      <span class="icon">
        <i class="fas" :class="expanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
      </span>
    </button>

    <div v-if="expanded" class="picker-panel" data-test="picker-panel">
      <div class="picker-top">
        <div class="field has-addons is-rounded mb-0 picker-search-field">
          <div class="control has-icons-left is-expanded">
            <span class="icon is-left has-text-info">
              <i class="fas fa-search"></i>
            </span>
            <input
              class="input is-rounded"
              type="search"
              placeholder="Search ingredients..."
              :value="query"
              data-test="picker-search"
              @input="onQueryInput"
            />
          </div>
        </div>
        <div v-if="selectedCount > 0" class="picker-actions">
          <a class="has-text-link" data-test="picker-clear" @click.prevent="clearSelection">
            Clear
          </a>
          <button
            type="button"
            class="button is-primary"
            data-test="picker-add"
            @click="addSelected"
          >
            Add {{ selectedCount }} to order
          </button>
        </div>
      </div>

      <div class="picker-list" :class="{ loading }">
        <div v-if="loading" class="picker-empty">Loading...</div>
        <div v-else-if="inventories.length === 0" class="picker-empty">No ingredients found.</div>
        <template v-else>
          <label class="picker-row picker-header">
            <input
              type="checkbox"
              :checked="allOnPageSelected"
              data-test="picker-select-page"
              @change="toggleAllOnPage"
            />
            <span class="picker-row-name">Select all on this page</span>
          </label>
          <label
            v-for="inv in inventories"
            :key="inv.id"
            class="picker-row"
            :data-test="`picker-row-${inv.id}`"
          >
            <input
              type="checkbox"
              :checked="selectedItems.has(inv.id)"
              :data-test="`picker-checkbox-${inv.id}`"
              @change="toggleSelection(inv)"
            />
            <span class="picker-row-name">{{ inv.ingredient.name }}</span>
            <button
              type="button"
              class="button is-primary picker-row-add"
              :data-test="`picker-row-add-${inv.id}`"
              title="Add to order"
              @click.prevent="addOne(inv)"
            >
              <span class="icon">
                <i class="fas fa-plus"></i>
              </span>
              <span>Add</span>
            </button>
          </label>
        </template>
      </div>

      <div class="picker-footer">
        <div class="picker-pagination">
          <button
            type="button"
            class="button is-small"
            :disabled="page <= 1 || loading"
            data-test="picker-prev"
            @click="goToPage(page - 1)"
          >
            Prev
          </button>
          <span class="picker-page-label" data-test="picker-page-label">
            Page {{ page }} of {{ totalPages }}
          </span>
          <button
            type="button"
            class="button is-small"
            :disabled="page >= totalPages || loading"
            data-test="picker-next"
            @click="goToPage(page + 1)"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @import "~jose_modern/css";

  .supplier-inventory-picker {
    display: flex;
    flex-direction: column;
  }

  .picker-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    width: 100%;
    padding: 0.25rem 0;
    background: none;
    border: none;
    color: $text-strong;
    cursor: pointer;

    &:hover .picker-heading-label {
      color: $link;
    }
  }

  .picker-heading-text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.125rem;
    text-align: left;
  }

  .picker-heading-label {
    font-size: 1rem;
    font-weight: 600;
  }

  .picker-heading-supplier {
    font-weight: 700;
  }

  .picker-heading-subtitle {
    font-size: 0.85rem;
  }

  .picker-panel {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-top: 1rem;
  }

  .picker-top {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .picker-search-field {
    flex: 1 1 16rem;
    min-width: 0;
  }

  .picker-list.loading {
    opacity: 0.6;
  }

  .picker-empty {
    padding: 1rem;
    text-align: center;
    color: $grey;
  }

  .picker-row {
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: 1rem;
    align-items: center;
    padding: 0.5rem 0.25rem;
    border-bottom: 1px solid $border-light;
    cursor: pointer;

    &:last-child {
      border-bottom: none;
    }
  }

  .picker-header {
    font-weight: 600;
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
    color: $grey-dark;
  }

  .picker-footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
  }

  .picker-pagination {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .picker-page-label {
    font-size: 0.875rem;
    color: $grey-dark;
  }

  .picker-actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
</style>
